import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useCompany } from "../../hooks/useCompany";
import { CompanyStatus } from "../../types/company";
import Button from "../global/Button";

const DashboardWelcomeHeader = () => {
  const user = useCurrentUser();
  const company = useCompany();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        mb: { xs: 2, sm: 4 },
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: 2, sm: 4 },
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)",
        border: (theme) =>
          `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)"}`,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0, 0, 0, 0.4)"
            : "0 8px 32px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background:
            "linear-gradient(90deg, transparent, rgba(46, 125, 50, 0.6), transparent)",
        },
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          zIndex: 1,
          width: { xs: "100%", sm: "auto" },
          mb: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            mb: { xs: 0.5, sm: 1 },
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.25rem" },
            color: "text.primary",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Welcome Back, {user?.full_name} 👋
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            mt: { xs: 1, sm: 1.5 },
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
            }}
          >
            {user?.company?.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              display: { xs: "none", sm: "block" },
            }}
          >
            •
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            {dayjs().format("ddd, MMMM DD YYYY z")}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate(`/works/create`)}
        disabled={company?.status != CompanyStatus.ACTIVE}
        sx={{
          fontWeight: 700,
          textTransform: "none",
          borderRadius: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 1.25, sm: 1.5, md: 1.75 },
          boxShadow: 4,
          fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
          position: "relative",
          zIndex: 1,
          width: { xs: "100%", sm: "auto" },
          "&:hover": {
            boxShadow: 8,
            transform: "translateY(-3px)",
          },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        + Create New Work
      </Button>
    </Box>
  );
};

export default DashboardWelcomeHeader;
